import { motion } from "framer-motion";
import { Mail, Phone, MapPin } from "lucide-react";
import { useState } from "react";
import { Helmet } from "react-helmet";

const Contact = () => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email || !formData.phone) {
      setStatus("Please fill in Email and Phone.");
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append("name", formData.name);
    formDataToSend.append("email", formData.email);
    formDataToSend.append("phone", formData.phone);
    formDataToSend.append("message", formData.message);
    formDataToSend.append("subject", "Contact Form Submission");

    fetch('submit_form.php', {
          method: 'POST',
          body: formDataToSend,
        })
        .then(response => response.text())
        .then(data => {
          console.log(data); // Log the response from PHP
          // Handle success or display a message to the user
          alert("Message sent successfully");
          setFormData({
            name: '',
            email: '',
            phone: '',
            message: ''
          });
        })
        .catch(error => {
          console.error('Error:', error);
          // Handle error case
        });
      };
      
  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 to-white">
      <Helmet>
        <title>Contact Shree Engineering | Get in Touch</title>
        <meta name="description" content="Contact Shree Engineering for technical consultancy, engineering solutions, manufacturing, fabrication, heat treatment, spray booth, vacuum oven, panel board, electrical supplies, or project inquiries." />
        <meta name="keywords" content="Contact Shree Engineering, Inquiry, Technical Consultancy, Engineering Solutions, Manufacturing, Fabrication, Heat Treatment, Spray Booth, Vacuum Oven, Panel Board, Electrical Supplies" />
      
        {/* Open Graph */}
        <meta property="og:title" content="Contact Shree Engineering | Get in Touch" />
        <meta property="og:description" content="Reach out to Shree Engineering for consultancy, project management, manufacturing, fabrication, heat treatment, spray booth, vacuum oven, panel board, electrical supplies, and engineering solutions." />
        <meta property="og:image" content="https://www.shree-eng.com/images/og-contact.jpg" />
        <meta property="og:url" content="https://www.shree-eng.com/contact" />
        <meta property="og:type" content="website" />
      
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Shree Engineering | Get in Touch" />
        <meta name="twitter:description" content="Reach out to Shree Engineering for consultancy, project management, manufacturing, fabrication, heat treatment, spray booth, vacuum oven, panel board, electrical supplies, and engineering solutions." />
        <meta name="twitter:image" content="https://www.shree-eng.com/images/og-contact.jpg" />
      
        {/* Contact structured data with all services */}
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Shree Engineering",
            "url": "https://www.shree-eng.com",
            "logo": "https://www.shree-eng.com/images/logo.png",
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+91-9972529464",
              "contactType": "Customer Service",
              "email": "info@shree-eng.com"
            },
            "makesOffer": [
              {"@type": "Offer","itemOffered":{"@type":"Service","name":"Technical Consultancy"}},
              {"@type": "Offer","itemOffered":{"@type":"Service","name":"Engineering Solutions"}},
              {"@type": "Offer","itemOffered":{"@type":"Service","name":"Manufacturing"}},
              {"@type": "Offer","itemOffered":{"@type":"Service","name":"Fabrication"}},
              {"@type": "Offer","itemOffered":{"@type":"Service","name":"Heat Treatment"}},
              {"@type": "Offer","itemOffered":{"@type":"Service","name":"Spray Booth"}},
              {"@type": "Offer","itemOffered":{"@type":"Service","name":"Vacuum Oven"}},
              {"@type": "Offer","itemOffered":{"@type":"Service","name":"Panel Board"}},
              {"@type": "Offer","itemOffered":{"@type":"Service","name":"Electrical Supplies"}}
            ]
          }
          `}
        </script>
      </Helmet>
      {/* Background Shapes */}
      <div className="absolute top-10 left-10 w-40 h-40 bg-blue-200 rounded-full opacity-20 animate-pulse" />
      <div className="absolute bottom-20 right-20 w-60 h-60 bg-orange-200 rounded-full opacity-20 animate-bounce" />
      <div className="absolute top-1/2 left-1/2 w-72 h-72 bg-blue-100 rounded-full opacity-10 animate-spin-slow -z-10" />

      <div className="max-w-7xl mx-auto px-6 py-16 relative z-10 grid md:grid-cols-2 gap-10">
        {/* Left Section */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="flex flex-col justify-center"
        >
          <h1 className="text-4xl font-bold text-blue-900 mb-6">
            Contact Us
          </h1>
          <p className="text-gray-600 mb-8">
            Get in touch with Shree Engineering. We’d love to hear from you.
          </p>

          <div className="space-y-6">
            {/* Card 1 */}
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="flex items-center gap-4 bg-white p-5 rounded-xl shadow-lg"
            >
              <div className="bg-blue-100 p-3 rounded-full">
                <Mail className="w-6 h-6 text-blue-900" />
              </div>
              <p className="text-gray-700">contact@shree-eng.com</p>
            </motion.div>

            {/* Card 1 */}
<motion.div
  whileHover={{ scale: 1.05 }}
  className="flex items-center gap-4 bg-white p-5 rounded-xl shadow-lg"
>
  <div className="bg-blue-100 p-3 rounded-full">
    {/* Assuming Phone is a valid component */}
    <Phone className="w-6 h-6 text-blue-900" />
  </div>
  <p className="text-gray-700 flex items-center gap-2">
    +966 54 764 6782 
    <span className="bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full">International</span>
  </p>
</motion.div>

{/* Card 2 */}
<motion.div
  whileHover={{ scale: 1.05 }}
  className="flex items-center gap-4 bg-white p-5 rounded-xl shadow-lg"
>
  <div className="bg-blue-100 p-3 rounded-full">
    {/* Assuming Phone is a valid component */}
    <Phone className="w-6 h-6 text-blue-900" />
  </div>
  <p className="text-gray-700">+91 99725 29464</p>
</motion.div>

            {/* Card 3 */}
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="flex items-center gap-4 bg-white p-5 rounded-xl shadow-lg"
            >
              <div className="bg-blue-100 p-3 rounded-full">
                <MapPin className="w-6 h-6 text-blue-900" />
              </div>
              <p className="text-gray-700">#B-265, 6th main, 2nd stage,
Peenya industrial area,
Bangalore - 560058.
Karnataka, India</p>
            </motion.div>
          </div>
        </motion.div>

        {/* Right Section (Form) */}
        <motion.form onSubmit={handleSubmit} className="bg-white shadow-lg rounded-xl p-8 grid gap-5">
          <motion.input
            type="text"
            name="name"
            placeholder="Your Name"
            value={formData.name}
            onChange={handleChange}
            className="p-3 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
            required
          />
          <motion.input
            type="email"
            name="email"
            placeholder="Your Email"
            value={formData.email}
            onChange={handleChange}
            className="p-3 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
            required
          />
          <motion.input
            type="tel"
            name="phone"
            placeholder="Your Phone"
            value={formData.phone}
            onChange={handleChange}
            className="p-3 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
            required
          />
          <motion.textarea
            name="message"
            placeholder="Your Message"
            rows={5}
            value={formData.message}
            onChange={handleChange}
            className="p-3 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none"
            required
          />
          <motion.button
            type="submit"
            className="bg-blue-900 text-white py-3 px-6 rounded-lg hover:bg-orange-500 transition"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Send Message
          </motion.button>
          {status && <p className="text-sm text-center mt-2 text-gray-600">{status}</p>}
        </motion.form>
      </div>
    </div>
  );
};

export default Contact;
